<template>
    <AdminLayout>
        <Head title="TRON Transaction Monitoring" />
        
        <div class="p-6 max-w-7xl mx-auto">
            <!-- Header -->
            <div class="mb-8">
                <h1 class="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                    <svg class="w-8 h-8 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0h6m-6 0v6"></path>
                    </svg>
                    TRON Transaction Monitoring & Sweep Management
                </h1>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Monitor deposits, manage sweeps, and manually run maintenance tasks
                </p>
            </div>

            <!-- Stats Dashboard -->
            <div class="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-blue-100 text-sm font-medium">Total Deposits</p>
                            <h3 class="text-3xl font-bold mt-2">{{ stats.total_deposits || 0 }}</h3>
                        </div>
                        <div class="bg-white/20 rounded-full p-3">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-green-100 text-sm font-medium">Approved</p>
                            <h3 class="text-3xl font-bold mt-2">{{ stats.approved_deposits || 0 }}</h3>
                        </div>
                        <div class="bg-white/20 rounded-full p-3">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-yellow-100 text-sm font-medium">Pending</p>
                            <h3 class="text-3xl font-bold mt-2">{{ stats.pending_deposits || 0 }}</h3>
                        </div>
                        <div class="bg-white/20 rounded-full p-3">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-purple-100 text-sm font-medium">Total Volume (USD)</p>
                            <h3 class="text-3xl font-bold mt-2">${{ formatNumber(stats.total_volume || 0) }}</h3>
                        </div>
                        <div class="bg-white/20 rounded-full p-3">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="bg-gradient-to-br from-indigo-500 to-blue-700 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-start justify-between">
                        <div>
                            <p class="text-indigo-100 text-sm font-medium">Funding Wallet TRX</p>
                            <h3 class="text-3xl font-bold mt-2">
                                <span v-if="fundingStatus.trx_balance !== null">{{ formatNumber(fundingStatus.trx_balance || 0) }}</span>
                                <span v-else-if="isLoadingFundingStatus">Loading...</span>
                                <span v-else>—</span>
                            </h3>
                            <p class="text-indigo-100 text-xs mt-2">
                                <span v-if="fundingStatus.has_funding_wallet && fundingStatus.funding_wallet_address">
                                    {{ truncateAddress(fundingStatus.funding_wallet_address) }}
                                </span>
                                <span v-else class="text-indigo-50/80">Not configured</span>
                            </p>
                        </div>
                        <div class="bg-white/20 rounded-full p-3">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <div v-if="fundingStatusError" class="mt-3 text-xs text-amber-100">
                        {{ fundingStatusError }}
                    </div>
                </div>
            </div>

            <!-- Auto-Funding Settings -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                        <svg class="w-6 h-6 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Auto-Funding Settings
                    </h2>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Current Settings Display -->
                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Current Configuration</h3>
                        <div class="space-y-2">
                            <div class="flex items-center justify-between">
                                <span class="text-sm text-gray-600 dark:text-gray-400">Mode:</span>
                                <span class="text-sm font-bold" :class="fundingStatus.auto_funding_mode === 'auto' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'">
                                    {{ fundingStatus.auto_funding_mode === 'auto' ? 'Automatic (Smart)' : 'Fixed Amount' }}
                                </span>
                            </div>
                            <div class="flex items-center justify-between">
                                <span class="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
                                <span class="text-sm font-bold text-gray-900 dark:text-white">
                                    {{ fundingStatus.auto_funding_amount ? fundingStatus.auto_funding_amount + ' TRX' : 'Auto-calculated' }}
                                </span>
                            </div>
                            <div class="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                <p v-if="fundingStatus.auto_funding_mode === 'auto'">
                                    ℹ️ System calculates exact deficit + 0.2 TRX buffer
                                </p>
                                <p v-else>
                                    ℹ️ Fixed amount sent every time (requires 0.8+ TRX)
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Settings Form -->
                    <div class="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border-2 border-indigo-200 dark:border-indigo-700">
                        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Update Settings</h3>
                        <form @submit.prevent="saveAutoFundingSettings" class="space-y-3">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Funding Mode</label>
                                <select v-model="autoFundingForm.mode" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                                    <option value="auto">Automatic (Recommended)</option>
                                    <option value="fixed">Fixed Amount</option>
                                </select>
                            </div>

                            <div v-if="autoFundingForm.mode === 'fixed'">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fixed Amount (TRX)</label>
                                <input 
                                    v-model.number="autoFundingForm.amount" 
                                    type="number" 
                                    step="0.1" 
                                    min="0.8" 
                                    max="10"
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                    placeholder="1.0"
                                />
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Must be between 0.8 and 10 TRX</p>
                            </div>

                            <button 
                                type="submit"
                                :disabled="isSavingFundingSettings"
                                class="w-full px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                                {{ isSavingFundingSettings ? 'Saving...' : 'Save Settings' }}
                            </button>
                        </form>
                    </div>
                </div>

                <!-- Success/Error Message -->
                <div v-if="fundingSettingsMessage" class="mt-4 p-3 rounded-lg text-sm" :class="fundingSettingsSuccess ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'">
                    {{ fundingSettingsMessage }}
                </div>
            </div>

            <!-- Manual Controls -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <svg class="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    Manual Controls
                </h2>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button 
                        @click="runMonitor"
                        :disabled="isRunningMonitor"
                        class="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        <svg v-if="!isRunningMonitor" class="w-12 h-12 text-blue-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                        <svg v-else class="w-12 h-12 text-blue-600 mb-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span class="text-lg font-bold text-gray-900 dark:text-white">{{ isRunningMonitor ? 'Running...' : 'Run Monitor' }}</span>
                        <span class="text-sm text-gray-600 dark:text-gray-400 mt-1">Scan for new transactions</span>
                    </button>

                    <button 
                        @click="refreshDeposits"
                        :disabled="isLoadingDeposits"
                        class="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        <svg v-if="!isLoadingDeposits" class="w-12 h-12 text-green-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <svg v-else class="w-12 h-12 text-green-600 mb-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span class="text-lg font-bold text-gray-900 dark:text-white">{{ isLoadingDeposits ? 'Loading...' : 'Refresh Status' }}</span>
                        <span class="text-sm text-gray-600 dark:text-gray-400 mt-1">Update sweep status</span>
                    </button>

                    <button 
                        @click="refreshStats"
                        :disabled="isLoadingStats"
                        class="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        <svg v-if="!isLoadingStats" class="w-12 h-12 text-purple-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                        </svg>
                        <svg v-else class="w-12 h-12 text-purple-600 mb-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span class="text-lg font-bold text-gray-900 dark:text-white">{{ isLoadingStats ? 'Loading...' : 'Refresh Stats' }}</span>
                        <span class="text-sm text-gray-600 dark:text-gray-400 mt-1">Update dashboard</span>
                    </button>
                </div>

                <!-- Monitor Output -->
                <div v-if="monitorOutput" class="mt-6 bg-gray-900 rounded-lg p-4 overflow-auto max-h-96">
                    <div class="flex items-center justify-between mb-2">
                        <h3 class="text-sm font-bold text-green-400">Monitor Output:</h3>
                        <button @click="monitorOutput = null" class="text-gray-400 hover:text-white">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    <pre class="text-xs text-green-300 font-mono whitespace-pre-wrap">{{ monitorOutput }}</pre>
                </div>
            </div>

            <!-- Deposits Table -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div class="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div>
                            <h2 class="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                                <svg class="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
                                </svg>
                                Recent Deposits (Last 30 Days)
                            </h2>
                            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Showing {{ deposits.length }} deposits • 
                                <span class="text-red-600 dark:text-red-400 font-semibold">{{ unsweptCount }} unswept</span> • 
                                <span class="text-green-600 dark:text-green-400 font-semibold">{{ sweptCount }} swept</span>
                            </p>
                        </div>
                        <div class="flex items-center space-x-3">
                            <select v-model="filterStatus" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                <option value="all">All Deposits</option>
                                <option value="unswept">Unswept Only</option>
                                <option value="swept">Swept Only</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="overflow-x-auto">
                    <table class="w-full">
                        <thead class="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Deposit ID</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount (USDT)</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">USDT Balance</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">TRX Balance</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="deposit in filteredDeposits" :key="deposit.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="text-sm font-medium text-gray-900 dark:text-white">{{ deposit.trx }}</div>
                                    </div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                        {{ deposit.trx_hash ? deposit.trx_hash.substring(0, 16) + '...' : 'N/A' }}
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ deposit.user_name }}</div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400">{{ deposit.user_email }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-bold text-gray-900 dark:text-white">{{ deposit.crypto_amount }} USDT</div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400">${{ deposit.amount }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-bold" :class="deposit.current_balance >= 1 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'">
                                        {{ deposit.current_balance.toFixed(4) }} USDT
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <!-- Show check button if TRX balance not loaded, otherwise show balance -->
                                    <div v-if="deposit.trx_balance === null" class="text-xs">
                                        <button 
                                            @click="checkTrxBalance(deposit)"
                                            :disabled="deposit.checking_trx"
                                            class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline disabled:opacity-50"
                                        >
                                            {{ deposit.checking_trx ? 'Checking...' : 'Check TRX' }}
                                        </button>
                                    </div>
                                    <div v-else>
                                        <div class="text-sm font-medium" :class="deposit.trx_balance >= 1 ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'">
                                            {{ deposit.trx_balance.toFixed(2) }} TRX
                                        </div>
                                        <div v-if="deposit.trx_balance < 1 && deposit.current_balance >= 1" class="text-xs text-orange-600 dark:text-orange-400">
                                            ⚠️ Low gas
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span v-if="deposit.swept" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                                        </svg>
                                        Swept
                                    </span>
                                    <span v-else-if="deposit.current_balance >= 0.1" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                        <svg class="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                                        </svg>
                                        Needs Sweep
                                    </span>
                                    <span v-else class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
                                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd"></path>
                                        </svg>
                                        Low Balance
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {{ formatDate(deposit.created_at) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <button 
                                        v-if="!deposit.swept && deposit.current_balance >= 0.1"
                                        @click="sweepDeposit(deposit)"
                                        :disabled="deposit.is_sweeping || deposit.trx_balance < 1"
                                        class="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        :title="deposit.trx_balance < 0.8 ? 'Insufficient TRX for fees (need 0.8+ TRX)' : 'Sweep USDT to main wallet'">
                                        <svg v-if="!deposit.is_sweeping" class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                        </svg>
                                        <svg v-else class="w-4 h-4 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                        </svg>
                                        {{ deposit.is_sweeping ? 'Sweeping...' : 'Sweep Now' }}
                                    </button>
                                    <span v-else-if="deposit.swept" class="text-green-600 dark:text-green-400 font-medium">
                                        ✓ Complete
                                    </span>
                                    <span v-else-if="deposit.trx_balance < 1 && deposit.current_balance >= 0.1" class="text-orange-600 dark:text-orange-400 text-xs">
                                        Need TRX for fees
                                    </span>
                                    <span v-else class="text-gray-400">
                                        N/A
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="filteredDeposits.length === 0" class="p-12 text-center">
                    <svg class="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                    </svg>
                    <p class="text-gray-500 dark:text-gray-400 font-medium">No deposits found</p>
                </div>
            </div>
        </div>

        <!-- Confirmation Dialog -->
        <ConfirmDialog
            v-model="confirmDialog.show"
            :title="confirmDialog.title"
            :message="confirmDialog.message"
            :type="confirmDialog.type"
            :confirm-text="confirmDialog.confirmText"
            :loading="confirmDialog.loading"
            @confirm="handleDialogConfirm"
            @cancel="handleDialogCancel"
        />
    </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import AdminLayout from '@/Layouts/AdminLayout/AdminLayout.vue';
import ConfirmDialog from '@/Components/Admin/ConfirmDialog.vue';
import { useToast } from '@/composables/useToast.js';
import axios from 'axios';

const { success, error, warning, info } = useToast();

const stats = ref({
    total_deposits: 0,
    approved_deposits: 0,
    pending_deposits: 0,
    total_volume: 0,
    users_with_wallets: 0,
    exchange_rate: 0
});

const fundingStatus = ref({
    has_funding_wallet: false,
    funding_wallet_address: null,
    trx_balance: null,
    gateway_name: '',
    gateway_slug: ''
});

const fundingStatusError = ref(null);
const isLoadingFundingStatus = ref(false);

// Auto-funding settings
const autoFundingForm = ref({
    mode: 'auto',
    amount: 1.0
});
const isSavingFundingSettings = ref(false);
const fundingSettingsMessage = ref(null);
const fundingSettingsSuccess = ref(false);

const deposits = ref([]);
const filterStatus = ref('all');
const isLoadingDeposits = ref(false);
const isLoadingStats = ref(false);
const isRunningMonitor = ref(false);
const monitorOutput = ref(null);

// Confirmation dialog state
const confirmDialog = ref({
    show: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'Confirm',
    onConfirm: null,
    loading: false
});

const showConfirm = (options) => {
    return new Promise((resolve) => {
        confirmDialog.value = {
            show: true,
            title: options.title,
            message: options.message,
            type: options.type || 'info',
            confirmText: options.confirmText || 'Confirm',
            loading: false,
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false)
        };
    });
};

const handleDialogConfirm = async () => {
    if (confirmDialog.value.onConfirm) {
        confirmDialog.value.loading = true;
        await confirmDialog.value.onConfirm();
        confirmDialog.value.show = false;
        confirmDialog.value.loading = false;
    }
};

const handleDialogCancel = () => {
    if (confirmDialog.value.onCancel) {
        confirmDialog.value.onCancel();
    }
    confirmDialog.value.show = false;
};

const filteredDeposits = computed(() => {
    if (filterStatus.value === 'all') return deposits.value;
    if (filterStatus.value === 'unswept') return deposits.value.filter(d => !d.swept && d.current_balance >= 0.1);
    if (filterStatus.value === 'swept') return deposits.value.filter(d => d.swept);
    return deposits.value;
});

const unsweptCount = computed(() => {
    return deposits.value.filter(d => !d.swept && d.current_balance >= 0.1).length;
});

const sweptCount = computed(() => {
    return deposits.value.filter(d => d.swept).length;
});

const loadStats = async (withLoading = true) => {
    if (withLoading) {
        isLoadingStats.value = true;
    }
    try {
        const response = await axios.get('/admin/tron-wallets/monitoring-stats');
        if (response.data.success) {
            stats.value = response.data.stats;
            // Silently handle inactive gateway - don't show error
        }
    } catch (error) {
        console.error('Failed to load stats:', error);
        // Don't show alert if gateway is inactive
        if (error.response?.status !== 404) {
            alert('Failed to load statistics');
        }
    } finally {
        if (withLoading) {
            isLoadingStats.value = false;
        }
    }
};

const loadFundingStatus = async (withLoading = false) => {
    if (withLoading) {
        isLoadingFundingStatus.value = true;
    }
    fundingStatusError.value = null;

    try {
        const response = await axios.get('/admin/tron-wallets/funding-wallet-status');
        if (response.data.success) {
            fundingStatus.value = {
                has_funding_wallet: response.data.has_funding_wallet,
                funding_wallet_address: response.data.funding_wallet_address,
                trx_balance: response.data.trx_balance,
                gateway_name: response.data.gateway_name,
                gateway_slug: response.data.gateway_slug,
                auto_funding_mode: response.data.auto_funding_mode || 'auto',
                auto_funding_amount: response.data.auto_funding_amount || null
            };
            
            // Initialize auto-funding form from response
            autoFundingForm.value.mode = fundingStatus.value.auto_funding_mode;
            autoFundingForm.value.amount = fundingStatus.value.auto_funding_amount || 1.0;
        } else {
            fundingStatusError.value = response.data.message || 'Funding wallet unavailable';
        }
    } catch (err) {
        console.error('Failed to load funding wallet status:', err);
        fundingStatusError.value = err.response?.data?.message || 'Failed to load funding wallet status';
    } finally {
        if (withLoading) {
            isLoadingFundingStatus.value = false;
        }
    }
};

const refreshStats = async () => {
    isLoadingStats.value = true;
    try {
        await Promise.all([
            loadStats(false),
            loadFundingStatus(false)
        ]);
    } finally {
        isLoadingStats.value = false;
    }
};

const loadDeposits = async (force = false) => {
    // Skip if already loading
    if (isLoadingDeposits.value && !force) {
        return;
    }
    
    isLoadingDeposits.value = true;
    try {
        // Reduced timeout since we limited balance checks in backend
        const response = await axios.get('/admin/tron-wallets/unswept-deposits', {
            timeout: 60000 // 60 seconds (reduced from 120)
        });
        if (response.data.success) {
            const toNumber = (val, fallback = 0) => {
                const num = Number(val);
                return Number.isFinite(num) ? num : fallback;
            };

            deposits.value = response.data.deposits.map(d => ({
                ...d,
                current_balance: toNumber(d.current_balance, 0),
                crypto_amount: toNumber(d.crypto_amount, 0),
                amount: toNumber(d.amount, 0),
                trx_balance: d.trx_balance === null ? null : toNumber(d.trx_balance, 0),
                is_sweeping: false,
                checking_trx: false
            }));
            // Silently handle inactive gateway - don't show error
        }
    } catch (error) {
        console.error('Failed to load deposits:', error);
        // Don't show alert if gateway is inactive
        if (error.response?.status !== 404) {
            alert('Failed to load deposits');
        }
    } finally {
        isLoadingDeposits.value = false;
    }
};

const refreshDeposits = async () => {
    await loadDeposits();
    await refreshStats();
};

const checkTrxBalance = async (deposit) => {
    deposit.checking_trx = true;
    try {
        const response = await axios.get(`/admin/tron-wallets/check-trx-balance/${deposit.id}`, {
            timeout: 30000 // 30 seconds
        });
        if (response.data.success) {
            deposit.trx_balance = response.data.trx_balance;
            deposit.usdt_balance = response.data.usdt_balance;
        }
    } catch (error) {
        console.error('Failed to check TRX balance:', error);
        alert('Failed to check TRX balance');
    } finally {
        deposit.checking_trx = false;
    }
};

const runMonitor = async () => {
    const confirmed = await showConfirm({
        title: 'Execute TRON Wallet Monitor?',
        message: 'This operation will:\n\n• Scan all user wallets for new transactions\n• Auto-approve eligible deposits\n• Sweep approved deposits to the main gateway wallet\n• Check wallet balances and update statuses\n\nThis process may take several seconds to complete.',
        type: 'info',
        confirmText: 'Execute Monitor'
    });

    if (!confirmed) return;

    isRunningMonitor.value = true;
    monitorOutput.value = 'Initializing wallet monitoring system...\n';

    try {
        const response = await axios.post('/admin/tron-wallets/run-monitor', {
            auto_approve: true,
            sweep: true
        });

        if (response.data.success) {
            monitorOutput.value = response.data.output || 'Monitoring operation completed successfully';
            success('Monitoring command executed successfully! All deposits have been scanned and processed.');
            // Refresh data
            await refreshDeposits();
        } else {
            monitorOutput.value = response.data.output || 'Operation failed - see error details below';
            error('Monitoring command failed: ' + response.data.message);
        }
    } catch (err) {
        console.error('Failed to run monitor:', err);
        monitorOutput.value = '✗ System Error: ' + (err.response?.data?.message || err.message);
        error('Monitor execution failed: ' + (err.response?.data?.message || err.message));
    } finally {
        isRunningMonitor.value = false;
    }
};

const saveAutoFundingSettings = async () => {
    // Clear previous messages
    fundingSettingsMessage.value = null;
    fundingSettingsSuccess.value = false;
    
    // Validate fixed amount if in fixed mode
    if (autoFundingForm.value.mode === 'fixed') {
        if (!autoFundingForm.value.amount || autoFundingForm.value.amount < 0.8 || autoFundingForm.value.amount > 10) {
            fundingSettingsMessage.value = 'Fixed amount must be between 0.8 and 10 TRX';
            fundingSettingsSuccess.value = false;
            return;
        }
    }
    
    isSavingFundingSettings.value = true;
    
    try {
        const response = await axios.post('/admin/tron-wallets/auto-funding-settings', {
            mode: autoFundingForm.value.mode,
            amount: autoFundingForm.value.mode === 'fixed' ? autoFundingForm.value.amount : null
        });
        
        if (response.data.success) {
            fundingSettingsMessage.value = response.data.message || 'Settings saved successfully';
            fundingSettingsSuccess.value = true;
            
            // Update funding status with new values
            fundingStatus.value.auto_funding_mode = response.data.mode;
            fundingStatus.value.auto_funding_amount = response.data.amount;
            
            // Show success notification
            success('Auto-funding settings have been updated');
            
            // Clear message after 3 seconds
            setTimeout(() => {
                fundingSettingsMessage.value = null;
            }, 3000);
        } else {
            fundingSettingsMessage.value = response.data.message || 'Failed to save settings';
            fundingSettingsSuccess.value = false;
        }
    } catch (err) {
        console.error('Failed to save auto-funding settings:', err);
        fundingSettingsMessage.value = err.response?.data?.message || 'An error occurred while saving settings';
        fundingSettingsSuccess.value = false;
        error('Failed to update auto-funding settings');
    } finally {
        isSavingFundingSettings.value = false;
    }
};

const sweepDeposit = async (deposit) => {
    const confirmed = await showConfirm({
        title: 'Manual Sweep Confirmation',
        message: `Deposit Details:\n\n• User: ${deposit.user_name}\n• Amount: ${deposit.current_balance.toFixed(4)} USDT\n• Current Balance: ${deposit.current_balance.toFixed(4)} USDT\n• Status: ${deposit.status}\n\nThis will transfer ${deposit.current_balance.toFixed(4)} USDT from the user's wallet to the main gateway wallet.\n\nProceed with sweep operation?`,
        type: 'warning',
        confirmText: 'Sweep Now'
    });

    if (!confirmed) return;

    deposit.is_sweeping = true;

    try {
        const response = await axios.post(`/admin/tron-wallets/sweep-deposit/${deposit.id}`);

        if (response.data.success) {
            success(`Successfully swept ${response.data.data.amount_swept} USDT! Transaction: ${response.data.data.tx_hash.substring(0, 20)}...`);
            // Refresh deposit status
            await loadDeposits();
        } else {
            error('Sweep operation failed: ' + response.data.message);
        }
    } catch (err) {
        console.error('Failed to sweep deposit:', err);
        error('Sweep execution failed: ' + (err.response?.data?.message || err.message));
    } finally {
        deposit.is_sweeping = false;
    }
};

const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const truncateAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
};

onMounted(() => {
    // Load data only once on mount
    Promise.all([refreshStats(), loadDeposits()]);
    
    // Auto-refresh every 60 seconds (reduced frequency)
    setInterval(() => {
        if (!isLoadingDeposits.value && !isRunningMonitor.value && !isLoadingStats.value) {
            // Only refresh if page is visible (user is viewing it)
            if (document.visibilityState === 'visible') {
                refreshStats();
            }
        }
    }, 60000); // Changed from 30s to 60s
});
</script>

<style scoped>
.animate-pulse-slow {
    animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
